<script>
	import { onMount, onDestroy } from "svelte";
	import { navigate } from "svelte-routing";
	import {
		Button,
		Modal,
		ModalBody,
		ModalFooter,
		ModalHeader,
	} from "@sveltestrap/sveltestrap";
	import CustomTable from "src/components/CustomTable/CustomTable.svelte";
	import {
		initPage,
		totalRecords,
		totalPages,
		sizeSelect,
		toastMessage,
	} from "src/store/store";
	import {
		getWorkOrderById,
		getWorkOrders,
		updateWorkOrderStatus,
	} from "src/services/maintenanceRepair";
	import { withoutWorkshopOnlyTransitions } from "src/utils/misc";

	import VehicleWorkOrdersFilterForm from "./VehicleWorkOrdersFilterForm.svelte";
	import MarkAsCompletedModal from "../../WorkOrderDetail/MarkAsCompletedModal.svelte";
	import CancelWorkOrderModal from "../../WorkOrderDetail/CancelWorkOrderModal.svelte";
	import CancelWorkOrderBlockedModal from "../../WorkOrderDetail/CancelWorkOrderBlockedModal.svelte";
	import {
		statusChangeErrorMessage,
		markWorkOrderCompleted,
		completedToastMessage,
		cancelWorkOrder,
	} from "../../WorkOrderDetail/workOrderStatusActions";

	export let vehicleId;

	let params = {};
	let allRows = [];

	let isWorkshopConflictModalOpen = false;
	let conflictingWorkOrderCode = "";

	let isMarkAsCompletedModalOpen = false;
	let isCancelWorkOrderModalOpen = false;
	let isCancelWorkOrderBlockedModalOpen = false;
	let rowForStatusChange = null;
	let workOrderForModal = null;

	function changeSearchParams(newParams) {
		params = { ...params, ...newParams };
		initPage.set(1);
	}

	function handleSort({ detail: { column, direction } }) {
		changeSearchParams({ sortCol: column, sortDir: direction });
	}

	function handleRowClick(event) {
		navigate(`/maintenance-repair/${vehicleId}/details/${event.detail.id}`);
	}

	function closeWorkshopConflictModal() {
		isWorkshopConflictModalOpen = false;
	}

	// EL-1181: only the terminal transitions (Completed/Cancelled) need
	// extra input, so they get their own confirmation modals here too —
	// mirrors WorkOrderDetail.svelte's handleStatusChange.
	// Bumped on every status selection, so a slow Completed detail fetch can
	// tell whether anything (even another choice on the same row) came after.
	let latestStatusSelection = 0;

	async function handleStatusChange({ detail: { row, newStatus } }) {
		const selection = ++latestStatusSelection;
		if (newStatus === "Completed") {
			rowForStatusChange = row;
			let res;
			try {
				res = await getWorkOrderById(row.id);
			} catch (error) {
				if (selection !== latestStatusSelection) return;
				console.error(error);
			}
			if (selection !== latestStatusSelection) return;
			if (!res?.data) {
				toastMessage.set({
					type: "danger",
					message: "Something went wrong. Please try again.",
				});
				return;
			}
			workOrderForModal = res.data;
			isMarkAsCompletedModalOpen = true;
			return;
		}
		if (newStatus === "Cancelled") {
			rowForStatusChange = row;
			if (row.issueCount > 0) {
				isCancelWorkOrderBlockedModalOpen = true;
				return;
			}
			isCancelWorkOrderModalOpen = true;
			return;
		}

		const res = await updateWorkOrderStatus(row.id, newStatus);
		if (res.error === "WORKSHOP_CONFLICT") {
			conflictingWorkOrderCode = res.conflictingWorkOrder.code;
			isWorkshopConflictModalOpen = true;
			return;
		}
		allRows = allRows.map((r) =>
			r.id === row.id ? { ...r, ...res.data } : r
		);
	}

	function closeMarkAsCompletedModal() {
		isMarkAsCompletedModalOpen = false;
		rowForStatusChange = null;
		workOrderForModal = null;
	}

	async function confirmMarkAsCompleted({ detail: { setVehicleActive } }) {
		const row = rowForStatusChange;
		const res = await markWorkOrderCompleted(
			row.id,
			row.status,
			setVehicleActive
		);
		isMarkAsCompletedModalOpen = false;
		rowForStatusChange = null;
		workOrderForModal = null;
		if (res.error) {
			toastMessage.set({
				type: "danger",
				message: statusChangeErrorMessage(res.error),
			});
			return;
		}

		allRows = allRows.map((r) =>
			r.id === row.id ? { ...r, ...res.data } : r
		);
		toastMessage.set({
			type: "success",
			message: completedToastMessage(res),
		});
	}

	function closeCancelWorkOrderModal() {
		isCancelWorkOrderModalOpen = false;
		rowForStatusChange = null;
	}

	async function confirmCancelWorkOrder({ detail: { reasonCode, notes } }) {
		const row = rowForStatusChange;
		const res = await cancelWorkOrder(
			row.id,
			row.status,
			reasonCode,
			notes
		);
		isCancelWorkOrderModalOpen = false;
		rowForStatusChange = null;
		// List rows don't carry jobs, so open jobs are only caught here.
		if (res.error?.code === 29203) {
			isCancelWorkOrderBlockedModalOpen = true;
			return;
		}
		if (res.error) {
			toastMessage.set({
				type: "danger",
				message: statusChangeErrorMessage(res.error, "cancel"),
			});
			return;
		}

		allRows = allRows.map((r) =>
			r.id === row.id ? { ...r, ...res.data } : r
		);
		toastMessage.set({
			type: "success",
			message: "Work order cancelled.",
		});
	}

	$: (async () => {
		const res = await getWorkOrders(vehicleId, {
			code: params.code,
			status: params.status,
			addedByEmail: params.addedByEmail,
			dateCreatedFrom: params.dateCreatedFrom,
			dateCreatedTo: params.dateCreatedTo,
			lastUpdatedFrom: params.lastUpdatedFrom,
			lastUpdatedTo: params.lastUpdatedTo,
			sortCol: params.sortCol,
			sortDir: params.sortDir,
		});
		allRows = res.data.items;
	})();

	$: filteredRows = allRows.filter((row) =>
		params.flagged === null || params.flagged === undefined
			? true
			: !!row.handoverFlagged === params.flagged
	);

	$: totalRecords.set(filteredRows.length);
	$: computedTotalPages = Math.max(
		1,
		Math.ceil(filteredRows.length / Number($sizeSelect))
	);
	$: totalPages.set(computedTotalPages);
	$: if ($initPage > computedTotalPages) initPage.set(computedTotalPages);
	$: rows = filteredRows
		.slice(
			($initPage - 1) * Number($sizeSelect),
			$initPage * Number($sizeSelect)
		)
		.map((row) => ({
			...row,
			allowedTransitions: withoutWorkshopOnlyTransitions(
				row.allowedTransitions
			),
		}));

	onMount(() => {
		initPage.set(1);
		sizeSelect.set("10");
	});

	onDestroy(() => {
		initPage.set(1);
		sizeSelect.set("20");
		totalPages.set(1);
		totalRecords.set(0);
	});
</script>

<div class="mb-4 mt-4">
	<VehicleWorkOrdersFilterForm {changeSearchParams} {params} />
</div>

<CustomTable
	columns={[
		{
			key: "code",
			label: "ID",
			type: "flagged",
			className: "link link-text",
			flagKey: "handoverFlagged",
			flagValue: true,
		},
		{ key: "issueCount", label: "Number of issues", width: "200px" },
		{ key: "jobCount", label: "Number of tasks", width: "200px" },
		{
			key: "lastUpdatedAt",
			label: "Last updated",
			type: "date",
			sortCol: "lastUpdatedAt",
		},
		{
			key: "createdAt",
			label: "Date created",
			type: "date",
			sortCol: "createdAt",
		},
		{
			key: "status",
			label: "Status",
			type: "status",
			options: "workOrder",
		},
		{ key: "addedBy", label: "Added by" },
	]}
	{rows}
	emptyMessage={`\nNo work orders\n\nGo to the 'Issues' tab to add issues to a new work order.`}
	useNavigation
	on:sort={handleSort}
	on:rowClick={handleRowClick}
	on:statusChange={handleStatusChange}
/>

<Modal
	isOpen={isWorkshopConflictModalOpen}
	toggle={closeWorkshopConflictModal}
	centered
>
	<ModalHeader toggle={closeWorkshopConflictModal}
		>Unable to change work order status</ModalHeader
	>
	<ModalBody>
		<p>Only 1 work order can have "IN WORKSHOP" status at a time.</p>
	</ModalBody>
	<ModalFooter>
		<Button
			class="themeButton primary"
			on:click={closeWorkshopConflictModal}
		>
			Back to work orders
		</Button>
	</ModalFooter>
</Modal>

<MarkAsCompletedModal
	isOpen={isMarkAsCompletedModalOpen}
	workOrder={workOrderForModal}
	on:close={closeMarkAsCompletedModal}
	on:confirm={confirmMarkAsCompleted}
/>

<CancelWorkOrderModal
	isOpen={isCancelWorkOrderModalOpen}
	on:close={closeCancelWorkOrderModal}
	on:confirm={confirmCancelWorkOrder}
/>

<CancelWorkOrderBlockedModal
	isOpen={isCancelWorkOrderBlockedModalOpen}
	toggle={() =>
		(isCancelWorkOrderBlockedModalOpen =
			!isCancelWorkOrderBlockedModalOpen)}
/>
